import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils/cn';

/*
 * StatusBadge (spec §7.5). Domain-free: the feature supplies the label and a
 * tone; this component knows nothing about specific statuses.
 *
 * Tone variants live here, not in components/ui/badge. Stock shadcn Badge ships
 * only default/secondary/destructive/outline; the semantic status tones
 * (success/warning/info) are an app opinion, so they belong at the composed
 * layer. They consume the `--success/--warning/--info` semantic tokens (which
 * stay in the design system) via first-class Tailwind utilities — no arbitrary
 * values.
 */
export type StatusTone =
  'neutral' | 'success' | 'warning' | 'info' | 'danger' | 'purple' | 'orange';

const TONE_CLASS: Record<StatusTone, string> = {
  neutral: '',
  success: 'border-transparent bg-success/15 text-success',
  warning: 'border-transparent bg-warning/15 text-warning',
  info: 'border-transparent bg-info/15 text-info',
  danger: 'border-transparent bg-destructive/15 text-destructive',
  purple: 'border-transparent bg-purple/15 text-purple',
  orange: 'border-transparent bg-orange/15 text-orange',
};

export function StatusBadge({
  label,
  tone = 'neutral',
}: {
  label: string;
  tone?: StatusTone;
}) {
  // Neutral falls back to the stock secondary variant; toned badges override
  // colors via semantic-token utilities while keeping Badge's shape/spacing.
  return (
    <Badge
      variant={tone === 'neutral' ? 'secondary' : 'outline'}
      className={cn(TONE_CLASS[tone])}
    >
      {label}
    </Badge>
  );
}
